/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { 
  Search, 
  HardHat,
  ChevronRight,
  Loader2,
  X,
  Play,
  Square,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  FolderOpen,
  Database,
  Building2,
  Package,
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';

// Shared components
import { 
  getMiningJobStatusConfig, 
  isMiningJobActive, 
  isMiningJobSpinning,
} from '../shared/statusConfig';

// Services
import {
  // R10: Import centralized query keys for cache coordination
  KNOWLEDGE_MINING_QUERY_KEYS,
  listMiningJobs,
  listMiningRuns,
  getMiningJob,
  createMiningJob,
  cancelMiningJob,
  listKnowledgeSources,
  createKnowledgeSource,
  validateSourceConfig,
  getMiningStrategies,
  resolveFilesystemPath,
} from '@/services/skillshop';
import type { 
  MiningJob, 
  MiningRun,
  KnowledgeSource, 
  SourceType,
  ChunkingStrategy,
  GenerationStrategy,
} from '@/services/skillshop';
import type { GetStrategiesResponseData } from '@shared/types/responses/knowledge-mining';


export function MiningJobsTab() {
  // R9: Persist filter state in URL for shareability and navigation survival
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read filters from URL with defaults
  const statusFilter = (searchParams.get('status') as 'all' | MiningJob['status']) || 'all';
  const selectedJobId = searchParams.get('jobId');
  
  const [newJobDialogOpen, setNewJobDialogOpen] = useState(false);
  const [newSourceDialogOpen, setNewSourceDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // R9: Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === 'all' || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }, [setSearchParams]);

  // Fetch active jobs (in-memory)
  const { data: activeJobs, isLoading: isLoadingActiveJobs } = useQuery({
    queryKey: ['mining-jobs', { status: statusFilter !== 'all' ? statusFilter : undefined }],
    queryFn: () => listMiningJobs({ status: statusFilter !== 'all' ? statusFilter : undefined }),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: (query) => {
      // Auto-refresh if any job is active
      const data = query.state.data as MiningJob[] | undefined;
      const hasActiveJob = data?.some(j => isMiningJobActive(j.status));
      return hasActiveJob ? 5000 : false;
    },
  });
  
  // Get active product for product-scoped runs
  const { activeProduct } = useApp();
  
  // Fetch historical runs (file-based history)
  // Pass activeProduct to include product-scoped runs in addition to org runs
  const { data: runs, isLoading: isLoadingRuns } = useQuery({
    queryKey: [...KNOWLEDGE_MINING_QUERY_KEYS.runs, { productId: activeProduct?.id }],
    queryFn: () => listMiningRuns({ productId: activeProduct?.id }),
    staleTime: 60 * 1000, // 1 minute
  });
  
  // Combine active jobs with historical runs
  // Active jobs take precedence if they exist
  const jobs: MiningJob[] = useMemo(() => {
    const combined: MiningJob[] = [];
    const seenIds = new Set<string>();
    
    // First add active jobs (higher priority)
    if (activeJobs) {
      for (const job of activeJobs) {
        combined.push(job);
        seenIds.add(job.id);
      }
    }
    
    // Then add historical runs that aren't already in active jobs
    if (runs) {
      for (const run of runs) {
        if (!seenIds.has(run.jobId)) {
          // Convert MiningRun to MiningJob format
          // Runs are completed historical records, so we provide default progress
          combined.push({
            jobId: run.jobId,
            id: run.jobId,
            sourceId: run.sourceId,
            sourceName: run.sourceName,
            sourceType: run.sourceType,
            status: run.status as MiningJob['status'],
            startedAt: run.startedAt,
            completedAt: run.completedAt,
            results: run.results ? {
              documentsProcessed: run.results.documentsProcessed,
              playbooksGenerated: run.results.playbooksGenerated,
              errors: 0, // Historical runs don't track errors
            } : undefined,
            strategy: undefined,
            progress: {
              phase: run.status as MiningJob['status'],
              current: run.results?.documentsProcessed ?? 0,
              total: run.results?.documentsProcessed ?? 0,
              percentage: 100,
            },
          });
          seenIds.add(run.jobId);
        }
      }
    }
    
    // Filter by status if needed
    if (statusFilter !== 'all') {
      return combined.filter(j => j.status === statusFilter);
    }
    
    // Sort by start date (most recent first)
    return combined.sort((a, b) => {
      const dateA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const dateB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [activeJobs, runs, statusFilter]);
  
  const isLoadingJobs = isLoadingActiveJobs || isLoadingRuns;
  
  // Find the selected job from the combined list
  // Note: Historical runs don't have details in the jobs API (they return 404)
  // so we only fetch details for active jobs that exist in the jobs endpoint
  const selectedJobFromList = selectedJobId 
    ? jobs?.find(j => j.id === selectedJobId) ?? null
    : null;
  
  // Only query job details for active jobs (not historical runs)
  // Active jobs are fetched from /api/knowledge-mining/jobs which has them in memory
  // Historical runs come from /api/knowledge-mining/runs and already have all info
  const isActiveJob = selectedJobFromList && activeJobs?.some(j => j.id === selectedJobId);
  
  const { 
    data: selectedJobDetails, 
    isLoading: isLoadingJobDetails,
  } = useQuery({
    queryKey: ['mining-job', selectedJobId],
    queryFn: () => getMiningJob(selectedJobId!),
    // Only fetch if it's an active job (exists in jobs API, not just runs)
    enabled: !!selectedJobId && !!isActiveJob,
    staleTime: 30 * 1000, // 30 seconds - jobs can change status
    refetchInterval: (query) => {
      // Auto-refresh if selected job is active
      const job = query.state.data;
      return job && isMiningJobActive(job.status) ? 5000 : false;
    },
  });
  
  // Use detailed job data if available (for active jobs), otherwise use list data
  const selectedJob = selectedJobId
    ? (isActiveJob ? selectedJobDetails : null) ?? selectedJobFromList
    : null;

  // Fetch sources
  const { data: sources, isLoading: isLoadingSources } = useQuery({
    queryKey: ['knowledge-sources'],
    queryFn: listKnowledgeSources,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch strategies
  const { data: strategies } = useQuery({
    queryKey: ['mining-strategies'],
    queryFn: getMiningStrategies,
    staleTime: 30 * 60 * 1000,
  });

  // Cancel job mutation - R10: Use centralized query keys
  const cancelJobMutation = useMutation({
    mutationFn: (id: string) => cancelMiningJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.jobs });
      toast({
        title: 'Job Cancelled',
        description: 'The mining job has been cancelled.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel job.',
        variant: 'destructive',
      });
    },
  });

  // R6 + R9: Select job by ID - React Query handles fetching details, URL persists selection
  const selectJob = useCallback((job: MiningJob) => {
    updateParams({ jobId: job.id });
  }, [updateParams]);

  // R6 + R9: Close focus view - React Query keeps data cached
  const closeFocusView = useCallback(() => {
    updateParams({ jobId: null });
  }, [updateParams]);

  return (
    <div className="flex h-[calc(100vh-320px)]">
      {/* Jobs List */}
      <div className={`flex flex-col ${selectedJobId ? 'w-1/2 border-r' : 'w-full'}`}>
        {/* Actions and Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <Button onClick={() => setNewJobDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Mining Job
            </Button>
            <Button variant="outline" onClick={() => setNewSourceDialogOpen(true)}>
              <Database className="h-4 w-4 mr-2" />
              Add Source
            </Button>
          </div>
          
          <div className="flex gap-2">
            {/* R9: Status filter persisted to URL */}
            <Select value={statusFilter} onValueChange={(v) => updateParams({ status: v })}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scanning">Scanning</SelectItem>
                <SelectItem value="extracting">Extracting</SelectItem>
                <SelectItem value="generating">Generating</SelectItem>
                <SelectItem value="review">In Review</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Jobs Cards */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoadingJobs ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : jobs?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <HardHat className="h-12 w-12 mb-4 opacity-50" />
              <p>No mining jobs found</p>
              <Button variant="link" onClick={() => setNewJobDialogOpen(true)}>
                Start a new job
              </Button>
            </div>
          ) : (
            jobs?.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isSelected={selectedJobId === job.id}
                onClick={() => selectJob(job)}
              />
            ))
          )}
        </div>
      </div>

      {/* Focus View (Job Detail) - R6: React Query handles loading/caching */}
      {selectedJobId && (
        isLoadingJobDetails && !selectedJob ? (
          <div className="w-1/2 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : selectedJob ? (
          <JobFocusView
            job={selectedJob}
            isLoadingDetails={isLoadingJobDetails}
            onClose={closeFocusView}
            onCancel={() => cancelJobMutation.mutate(selectedJob.id)}
            isCancelling={cancelJobMutation.isPending}
          />
        ) : null
      )}

      {/* New Job Dialog */}
      <NewJobDialog
        open={newJobDialogOpen}
        onOpenChange={setNewJobDialogOpen}
        sources={sources || []}
        strategies={strategies}
      />

      {/* New Source Dialog */}
      <NewSourceDialog
        open={newSourceDialogOpen}
        onOpenChange={setNewSourceDialogOpen}
      />
    </div>
  );
}

// Job Card Component
interface JobCardProps {
  job: MiningJob;
  isSelected: boolean;
  onClick: () => void;
}

function JobCard({ job, isSelected, onClick }: JobCardProps) {
  const config = getMiningJobStatusConfig(job.status);
  const StatusIcon = config.icon;

  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-primary bg-primary/5' : 'bg-slate-50 hover:border-gray-300'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <HardHat className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h3 className="font-medium truncate">{job.sourceName}</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge variant="outline" className={`text-xs ${config.cardClassName}`}>
              <StatusIcon className={`h-3 w-3 mr-1 ${isMiningJobSpinning(job.status) ? 'animate-spin' : ''}`} />
              {config.label}
            </Badge>
            {job.scope && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  job.scope === 'organization' 
                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                    : 'bg-violet-50 text-violet-700 border-violet-200'
                }`}
              >
                {job.scope === 'organization' ? (
                  <><Building2 className="h-3 w-3 mr-1" /> Org</>
                ) : (
                  <><Package className="h-3 w-3 mr-1" /> Product</>
                )}
              </Badge>
            )}
            {job.productId && (
              <Badge variant="outline" className="text-xs bg-gray-50">
                {job.productId}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs bg-gray-50">
              {job.sourceType}
            </Badge>
            {job.strategy && (
              <Badge variant="outline" className="text-xs">
                {job.strategy.generation}
              </Badge>
            )}
          </div>
          
          {/* Progress */}
          {isMiningJobActive(job.status) && job.progress && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span>
                <span>{job.progress.percentage}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${job.progress.percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {job.status === 'completed' && job.results && (
            <div className="mt-2 text-sm text-muted-foreground">
              {job.results.playbooksGenerated} playbooks generated
              {job.results.errors > 0 && (
                <span className="text-yellow-600 ml-2">
                  ({job.results.errors} errors)
                </span>
              )}
            </div>
          )}

          {/* Timing */}
          <div className="mt-2 text-xs text-muted-foreground">
            {job.startedAt && (
              <span>Started: {new Date(job.startedAt).toLocaleString()}</span>
            )}
            {job.durationSeconds && (
              <span className="ml-2">
                Duration: {Math.round(job.durationSeconds / 60)}m
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
      </div>
    </div>
  );
}

// Job Focus View Component
interface JobFocusViewProps {
  job: MiningJob;
  isLoadingDetails: boolean;
  onClose: () => void;
  onCancel: () => void;
  isCancelling: boolean;
}

function JobFocusView({ job, isLoadingDetails, onClose, onCancel, isCancelling }: JobFocusViewProps) {
  const config = getMiningJobStatusConfig(job.status);
  const StatusIcon = config.icon;

  return (
    <div className="w-1/2 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold truncate">{job.sourceName}</h2>
          <p className="text-sm text-muted-foreground">Job ID: {job.id}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Status Banner */}
        <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${config.bannerClassName}`}>
          <StatusIcon className={`h-5 w-5 ${isMiningJobSpinning(job.status) ? 'animate-spin' : ''}`} />
          <span className="font-medium">{config.label}</span>
        </div>

        {/* Cancel Button for active jobs */}
        {isMiningJobActive(job.status) && (
          <Button
            variant="destructive"
            onClick={onCancel}
            disabled={isCancelling}
            className="w-full mb-4"
          >
            {isCancelling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Square className="h-4 w-4 mr-2" />
            )}
            Cancel Job
          </Button>
        )}

        <div className="space-y-6">
          {/* Progress */}
          {isMiningJobActive(job.status) && job.progress && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Progress</h4>
              <div className="flex items-center justify-between text-sm mb-2">
                <span>{job.progress.current} / {job.progress.total}</span>
                <span className="font-medium">{job.progress.percentage}%</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${job.progress.percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Error message */}
          {job.status === 'failed' && job.errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-800 mb-2">Error</h4>
              <p className="text-sm text-red-700">{job.errorMessage}</p>
            </div>
          )}

          {/* Results */}
          {job.results && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Results</h4>
              <dl className="text-sm space-y-2">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Playbooks Generated:</dt>
                  <dd className="font-medium">{job.results.playbooksGenerated}</dd>
                </div>
                {job.results.errors > 0 && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-32">Errors:</dt>
                    <dd className="font-medium text-yellow-600">{job.results.errors}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Mining Run Scope */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Mining Run Scope</h4>
            <dl className="text-sm space-y-2">
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-32">Scope:</dt>
                <dd>
                  <Badge 
                    variant="outline" 
                    className={job.scope === 'organization' 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'bg-violet-50 text-violet-700'
                    }
                  >
                    {job.scope === 'organization' ? (
                      <><Building2 className="h-3 w-3 mr-1" /> Organization</>
                    ) : (
                      <><Package className="h-3 w-3 mr-1" /> Product</>
                    )}
                  </Badge>
                </dd>
              </div>
              {job.productId && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Product:</dt>
                  <dd>{job.productId}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-32">Storage:</dt>
                <dd className="text-xs font-mono text-muted-foreground">
                  {job.scope === 'organization' 
                    ? '~/.velocity/velocity-repo/vps/mining-runs/' 
                    : '{product-repo}/data/product/vps/mining-runs/'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Info about org-classified playbooks from product runs */}
          {job.scope === 'product' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> Playbooks classified as "organizational" from this product run 
              will be written to the organization playbooks in the Velocity repo.
            </div>
          )}

          {/* Strategy Configuration */}
          {job.strategy && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Strategy</h4>
              <dl className="text-sm space-y-2">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Chunking:</dt>
                  <dd>
                    <Badge variant="outline">{job.strategy.chunking}</Badge>
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Generation:</dt>
                  <dd>
                    <Badge variant="outline">{job.strategy.generation}</Badge>
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">AI Classification:</dt>
                  <dd>
                    <Badge variant="outline">
                      {job.strategy.useAiClassification ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Timing */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Timing</h4>
            <dl className="text-sm space-y-2">
              {job.startedAt && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Started:</dt>
                  <dd>{new Date(job.startedAt).toLocaleString()}</dd>
                </div>
              )}
              {job.completedAt && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Completed:</dt>
                  <dd>{new Date(job.completedAt).toLocaleString()}</dd>
                </div>
              )}
              {job.durationSeconds && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-32">Duration:</dt>
                  <dd>
                    {job.durationSeconds >= 60
                      ? `${Math.round(job.durationSeconds / 60)} minutes`
                      : `${job.durationSeconds} seconds`}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Source Info */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Source</h4>
            <dl className="text-sm space-y-2">
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-32">Name:</dt>
                <dd>{job.sourceName}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-32">Type:</dt>
                <dd>
                  <Badge variant="outline">{job.sourceType}</Badge>
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-32">Source ID:</dt>
                <dd className="text-xs font-mono">{job.sourceId}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

// New Job Dialog Component
interface NewJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: KnowledgeSource[];
  strategies?: GetStrategiesResponseData;
}

function NewJobDialog({ open, onOpenChange, sources, strategies }: NewJobDialogProps) {
  const { activeProduct } = useApp();
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [scope, setScope] = useState<'product' | 'organization'>('product');
  const [chunkingStrategy, setChunkingStrategy] = useState<ChunkingStrategy>('heuristic');
  const [generationStrategy, setGenerationStrategy] = useState<GenerationStrategy>('direct-llm');
  const [useAiClassification, setUseAiClassification] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createJobMutation = useMutation({
    mutationFn: () =>
      createMiningJob({
        sourceId: selectedSource,
        scope,
        productId: scope === 'product' ? activeProduct?.id : undefined,
        productRepoPath: scope === 'product' ? (activeProduct?.repoPath ?? undefined) : undefined,
        chunkingStrategy,
        generationStrategy,
        useAiClassification,
      }),
    onSuccess: () => {
      // R10: Use centralized query key
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.jobs });
      onOpenChange(false);
      setSelectedSource('');
      toast({
        title: 'Job Created',
        description: 'Mining job has been started.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create job.',
        variant: 'destructive',
      });
    },
  });

  const connectedSources = sources.filter(s => s.enabled);
  const canStartJob = selectedSource && (scope !== 'product' || activeProduct);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Mining Job</DialogTitle>
          <DialogDescription>
            Start a new knowledge mining job to extract playbooks from a source.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Mining Run Scope */}
          <div>
            <label className="text-sm font-medium mb-2 block">Mining Run Scope</label>
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={`flex-1 px-3 ${scope === 'organization' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => setScope('organization')}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Organization
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={`flex-1 px-3 ${scope === 'product' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => setScope('product')}
              >
                <Package className="h-4 w-4 mr-2" />
                Product
              </Button>
            </div>
            {scope === 'product' && activeProduct && (
              <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded text-sm text-violet-700">
                <span className="font-medium">Target:</span> {activeProduct.title}
                {activeProduct.productId && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {activeProduct.productId}
                  </Badge>
                )}
              </div>
            )}
            {scope === 'product' && !activeProduct && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                No active product. Select a product first or use Organization scope.
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Knowledge Source</label>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger>
                <SelectValue placeholder="Select a source" />
              </SelectTrigger>
              <SelectContent>
                {connectedSources.length === 0 ? (
                  <SelectItem value="none" disabled>No connected sources</SelectItem>
                ) : (
                  connectedSources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.type})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Chunking Strategy</label>
            <Select value={chunkingStrategy} onValueChange={(v) => setChunkingStrategy(v as ChunkingStrategy)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {strategies?.chunking.strategies.map(s => (
                  <SelectItem key={s.type} value={s.type}>{s.name}</SelectItem>
                )) || (
                  <>
                    <SelectItem value="heuristic">Heuristic</SelectItem>
                    <SelectItem value="rlm">RLM</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Generation Strategy</label>
            <Select value={generationStrategy} onValueChange={(v) => setGenerationStrategy(v as GenerationStrategy)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {strategies?.generation.strategies.map(s => (
                  <SelectItem key={s.type} value={s.type}>{s.name}</SelectItem>
                )) || (
                  <>
                    <SelectItem value="direct-llm">Direct LLM</SelectItem>
                    <SelectItem value="template">Template</SelectItem>
                    <SelectItem value="rlm">RLM</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="useAiClassification"
              checked={useAiClassification}
              onChange={(e) => setUseAiClassification(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="useAiClassification" className="text-sm">
              Use AI Classification
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createJobMutation.mutate()}
            disabled={!canStartJob || createJobMutation.isPending}
          >
            {createJobMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            Start Mining
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// New Source Dialog Component
interface NewSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Connection test status
interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: {
    filesystemInfo?: {
      basePath: string;
      fileCount: number;
      totalSize: number;
      extensions: string[];
    };
  };
}

function NewSourceDialog({ open, onOpenChange }: NewSourceDialogProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<SourceType>('filesystem');
  const [directory, setDirectory] = useState('');
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeProduct } = useApp();

  // Clear test result when config changes
  const handleDirectoryChange = (value: string) => {
    setDirectory(value);
    setTestResult(null);
  };

  const handleTypeChange = (value: SourceType) => {
    setType(value);
    setTestResult(null);
  };

  const createSourceMutation = useMutation({
    mutationFn: async () => {
      // Build the typed source input based on the discriminated union
      if (type === 'filesystem') {
        // Resolve relative paths to absolute using the active product's repo
        const resolved = await resolveFilesystemPath(
          directory,
          activeProduct?.repositoryUrl ?? undefined,
          activeProduct?.repoPath ?? undefined
        );
        
        return createKnowledgeSource({
          type: 'filesystem',
          name,
          config: { basePath: resolved.absolutePath },
        });
      }
      
      // TODO: Add support for confluence and gcs
      throw new Error(`Creating ${type} sources is not yet implemented`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources'] });
      onOpenChange(false);
      setName('');
      setDirectory('');
      setTestResult(null);
      toast({
        title: 'Source Created',
        description: 'Knowledge source has been created.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create source.',
        variant: 'destructive',
      });
    },
  });

  // Test connection before creation using validateSourceConfig
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      if (type === 'filesystem') {
        // Resolve relative paths to absolute using the active product's repo
        const resolved = await resolveFilesystemPath(
          directory,
          activeProduct?.repositoryUrl ?? undefined,
          activeProduct?.repoPath ?? undefined
        );
        
        const result = await validateSourceConfig({ type: 'filesystem', basePath: resolved.absolutePath });
        
        setTestResult(result);
        
        toast({
          title: result.success ? 'Connection Successful' : 'Connection Failed',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });
      } else {
        // TODO: Implement Confluence and GCS validation
        throw new Error(`Testing ${type} sources is not yet implemented`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection test failed';
      setTestResult({
        success: false,
        message,
      });
      toast({
        title: 'Connection Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Check if we can test (have minimum required config)
  const canTest = type === 'filesystem' && directory.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Knowledge Source</DialogTitle>
          <DialogDescription>
            Configure a new source for knowledge mining.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Name</label>
            <Input
              placeholder="Source name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Type</label>
            <Select value={type} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="filesystem">Filesystem</SelectItem>
                <SelectItem value="confluence">Confluence</SelectItem>
                <SelectItem value="gcs">Google Cloud Storage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === 'filesystem' && (
            <div>
              <label className="text-sm font-medium mb-2 block">Directory Path</label>
              <Input
                placeholder="docs (or /absolute/path)"
                value={directory}
                onChange={(e) => handleDirectoryChange(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {activeProduct?.repositoryUrl
                  ? "Relative paths (e.g., 'docs') resolve to the product repository"
                  : "Select a product to use relative paths, or enter an absolute path"}
              </p>
            </div>
          )}

          {type === 'confluence' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Confluence integration requires additional configuration in organization settings.
            </div>
          )}

          {type === 'gcs' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              GCS integration requires additional configuration in organization settings.
            </div>
          )}

          {/* Connection Test Result Display */}
          {testResult && (
            <div className={`rounded-lg p-3 text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <div className="font-medium">
                    {testResult.success ? 'Connection Valid' : 'Connection Invalid'}
                  </div>
                  <div className="mt-1">{testResult.message}</div>
                  {testResult.success && testResult.details?.filesystemInfo && (
                    <div className="mt-2 text-xs space-y-1">
                      <div>Files found: {testResult.details.filesystemInfo.fileCount}</div>
                      <div>
                        Extensions: {testResult.details.filesystemInfo.extensions.join(', ') || 'none'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={!canTest || isTesting}
          >
            {isTesting && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            Test Connection
          </Button>
          <Button
            onClick={() => createSourceMutation.mutate()}
            disabled={!name || (type === 'filesystem' && !directory) || createSourceMutation.isPending}
          >
            {createSourceMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            Add Source
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
